/**
 * App + router wiring (task 11.2; Req 1.3, 8.12, 8.13, 8.14).
 *
 * Routes:
 *   - `/`                                        → redirect to the newest run
 *   - `/run/:runDate/:runId`                     → Repo_List_View for that run
 *   - `/run/:runDate/:runId/repo/:repoName`      → Repo_Detail_View (deep link)
 *
 * Data flow (design "Data flow"):
 *   RunSelector (fetch runs.json) → useRunLoader(selection) (fetch index.json)
 *   → CompletenessBanner (loaded vs expected) → SearchBar + FilterPanel +
 *   RepoTable (driven by applyQuery over index.summaries) → RepoDetail (resolve
 *   detailKey from the index entry for :repoName).
 *
 * Search/filter/sort are preserved across detail navigation by carrying the
 * query state in the URL query string via the {@link encodeQueryState} /
 * {@link decodeQueryState} codec (Req 8.12): every navigation keeps
 * `location.search`, and the list re-derives its state from it. A deep link to
 * a repo opens its detail (Req 8.13); if the repo is not in the index, or its
 * detail fetch fails, the dashboard shows an "unavailable" indication and
 * navigates back to the list (Req 8.14).
 *
 * Security: this is a read-only SPA served (with its JSON data) from a private
 * S3 bucket through CloudFront + OAC; the browser never holds AWS credentials
 * and there is no authenticated surface to protect here.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";

import RunSelector, {
  fetchRunsCatalog,
  orderRuns,
  runKey,
} from "./components/RunSelector";
import CompletenessBanner from "./components/CompletenessBanner";
import SearchBar from "./components/SearchBar";
import FilterPanel from "./components/FilterPanel";
import RepoTable from "./components/RepoTable";
import RepoDetail from "./components/RepoDetail";
import ApplicationView from "./components/ApplicationView";
import { useRunLoader } from "./hooks/useRunLoader";
import { useAuthedFetchContext } from "./auth/AuthedFetchContext";
import { useSignOutContext } from "./auth/SignOutContext";
import { applyQuery, joinLibraryEntry } from "./lib/query";
import {
  deriveClassificationOptions,
  EMPTY_CLASSIFICATION_FILTERS,
  filterByClassification,
  type ClassificationFilterState,
} from "./lib/libraryFilter";
import { decodeQueryState, encodeQueryState } from "./lib/queryState";
import type { QueryState, RepoSummary, RunCatalogEntry } from "./types";

// ---------------------------------------------------------------------------
// Routing
// ---------------------------------------------------------------------------

/**
 * Top-level routes. A layout route ({@link DashboardChrome}) owns the runs
 * catalog and the run selector so they persist across the list/detail routes;
 * the inner routes render the list or detail for the selected run.
 */
export default function App() {
  return (
    <Routes>
      <Route element={<DashboardChrome />}>
        <Route index element={<RootRedirect />} />
        <Route path="run/:runDate/:runId" element={<RunPage />} />
        <Route path="run/:runDate/:runId/repo/:repoName" element={<RunPage />} />
        {/* ADDITIVE (task 15.5; Req 16.2, 16.7, 18.1): applications & diagrams
            view for a run. New route only — the list/detail routes above are
            untouched. */}
        <Route
          path="run/:runDate/:runId/applications"
          element={<ApplicationsPage />}
        />
      </Route>
      {/* Unknown paths fall back to the root, which redirects to the newest run. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ---------------------------------------------------------------------------
// Layout: runs catalog + run selector (shared across list/detail)
// ---------------------------------------------------------------------------

/** What the layout route shares with its child routes through the Outlet. */
interface DashboardContext {
  /** Ordered runs catalog (most-recent-first). Empty when none are available. */
  runs: RunCatalogEntry[];
  /** Token-attaching fetch for data requests (Req 3.2, 3.3, 3.4). */
  fetchFn: typeof fetch;
}

/** Read the shared dashboard context provided by {@link DashboardChrome}. */
function useDashboardContext(): DashboardContext {
  return useOutletContext<DashboardContext>();
}

type CatalogState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; runs: RunCatalogEntry[] };

/** Message shown when the runs catalog could not be loaded. */
export const CATALOG_ERROR_MESSAGE = "Scan results could not be loaded.";

/** Indication shown while the runs catalog is loading. */
export const CATALOG_LOADING_MESSAGE = "Loading scan runs\u2026";

/**
 * Layout component: fetches `dashboard/runs.json` once, renders the
 * {@link RunSelector}, and exposes the ordered catalog to child routes via the
 * Outlet. The selector's selected key is derived from the current route params
 * so the active run always reflects the URL; selecting a run navigates to its
 * list route while preserving the query string (Req 8.12).
 */
function DashboardChrome() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [catalog, setCatalog] = useState<CatalogState>({ status: "loading" });
  const fetchFn = useAuthedFetchContext();

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const result = await fetchRunsCatalog(controller.signal, undefined, fetchFn);
        setCatalog({ status: "ready", runs: orderRuns(result.runs ?? []) });
      } catch (caught) {
        if (controller.signal.aborted) return;
        const message = caught instanceof Error ? caught.message : String(caught);
        setCatalog({ status: "error", message });
      }
    })();
    return () => controller.abort();
  }, [fetchFn]);

  // The selected run key comes from the route (`/run/:runDate/:runId/...`).
  const selectedRunKey =
    params.runDate && params.runId
      ? runKey({ runDate: params.runDate, runId: params.runId })
      : null;

  // Selecting a run navigates to its list route, preserving the query string so
  // search/filter/sort carry over (Req 8.12).
  const onSelectRun = useCallback(
    (key: string) => {
      navigate({ pathname: `/run/${key}`, search: location.search });
    },
    [navigate, location.search],
  );

  if (catalog.status === "loading") {
    return (
      <Shell>
        <p role="status">{CATALOG_LOADING_MESSAGE}</p>
      </Shell>
    );
  }

  if (catalog.status === "error") {
    return (
      <Shell>
        <p role="alert" className="app-error-banner">
          {CATALOG_ERROR_MESSAGE}
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <RunSelector
        runs={catalog.runs}
        selectedRunKey={selectedRunKey}
        onSelect={onSelectRun}
      />
      <Outlet context={{ runs: catalog.runs, fetchFn } satisfies DashboardContext} />
    </Shell>
  );
}

/**
 * Page shell: a light-neutral canvas with a left navigation sidebar and a slim
 * white top app bar carrying the product title and an optional sign-out control
 * (task 5.6; Req 23.1, 23.2, 20.1). All visual values come from token-referencing
 * classes in `components.css`.
 */
function Shell({ children }: { children: React.ReactNode }) {
  const signOut = useSignOutContext();
  const location = useLocation();

  // Highlight "Applications" when the current route is the applications page,
  // and default to "Repositories" for the list/detail routes.
  const onApplicationsRoute = location.pathname.endsWith("/applications");

  return (
    <div className="app-canvas">
      <aside className="app-sidebar" aria-label="Primary">
        <div className="app-sidebar__brand">
          <span className="app-sidebar__brand-logo">Viamericas</span>
          <span className="app-sidebar__brand-title">ViaMind</span>
        </div>

        <nav className="app-sidebar__nav" aria-label="Sections">
          <SidebarItem
            icon={<HomeIcon />}
            label="Home"
            active={location.pathname === "/"}
            to="/"
          />
          <SidebarItem
            icon={<RepoIcon />}
            label="Repositories"
            active={!onApplicationsRoute && location.pathname !== "/"}
          />
          <SidebarItem
            icon={<AppsIcon />}
            label="Applications"
            active={onApplicationsRoute}
          />
          <SidebarItem icon={<ShieldIcon />} label="CodeShield" muted />
          <SidebarItem icon={<ThreatIcon />} label="Threat Modeling" muted />
        </nav>

        <button type="button" className="app-sidebar__collapse">
          <span aria-hidden="true">{"\u2039"}</span>
          <span>Collapse</span>
        </button>
      </aside>

      <div className="app-content">
        <header className="app-bar">
          <h1 className="app-title">
            <span className="app-title__brand">Viamericas</span>
            <span className="app-title__product">ViaMind</span>
          </h1>
          <div className="app-user">
            {signOut && (
              <button
                type="button"
                className="app-signout-btn"
                onClick={signOut}
                aria-label="Sign out"
              >
                Sign out
              </button>
            )}
          </div>
        </header>
        <main className="app-main">{children}</main>
      </div>
    </div>
  );
}

/**
 * A single sidebar entry. Rendered as a plain `<Link>` when `to` is provided,
 * otherwise as a static row (for placeholders that don't yet have a route).
 */
function SidebarItem({
  icon,
  label,
  active = false,
  muted = false,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  muted?: boolean;
  to?: string;
}) {
  const className = [
    "app-sidebar__nav-item",
    active ? "app-sidebar__nav-item--active" : null,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      <span className="app-sidebar__nav-icon" aria-hidden="true">
        {icon}
      </span>
      <span>{label}</span>
    </>
  );

  if (to) {
    return (
      <Link to={to} className={className} aria-current={active ? "page" : undefined}>
        {content}
      </Link>
    );
  }

  return (
    <span
      className={className}
      aria-current={active ? "page" : undefined}
      aria-disabled={muted ? "true" : undefined}
      style={muted ? { opacity: 0.55 } : undefined}
    >
      {content}
    </span>
  );
}

/* Simple inline SVG icons so the sidebar doesn't pull in an icon dependency. */
function HomeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h14V9.5" />
    </svg>
  );
}

function RepoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function AppsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 6h13" />
      <path d="M8 12h13" />
      <path d="M8 18h13" />
      <circle cx="4" cy="6" r="1" />
      <circle cx="4" cy="12" r="1" />
      <circle cx="4" cy="18" r="1" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3 4 6v6c0 5 4 8 8 9 4-1 8-4 8-9V6l-8-3Z" />
    </svg>
  );
}

function ThreatIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v4" />
      <path d="M12 18v4" />
      <path d="M4.9 4.9l2.8 2.8" />
      <path d="M16.3 16.3l2.8 2.8" />
      <path d="M2 12h4" />
      <path d="M18 12h4" />
      <path d="M4.9 19.1l2.8-2.8" />
      <path d="M16.3 7.7l2.8-2.8" />
    </svg>
  );
}

/**
 * Root route (`/`): redirect to the newest run. The {@link RunSelector} in the
 * layout already applies the newest-run default by calling `onSelect`, which
 * navigates here; while that resolves (or if the catalog is empty) this renders
 * nothing extra — the selector shows the no-runs message in the empty case
 * (Req 1.7).
 */
function RootRedirect() {
  const { runs } = useDashboardContext();
  if (runs.length === 0) {
    // Empty catalog: the RunSelector renders the "no scan results" message;
    // nothing more to show here.
    return null;
  }
  // Non-empty: the selector's default-selection effect navigates to the newest
  // run. Render a small placeholder until that navigation lands.
  return <p role="status">{CATALOG_LOADING_MESSAGE}</p>;
}

// ---------------------------------------------------------------------------
// Run page: list + detail for the selected run
// ---------------------------------------------------------------------------

/** Indication shown while a run's index is loading (Req 1.4, 11.2, 12.4). */
export const RUN_LOADING_MESSAGE = "Loading repositories\u2026";

/**
 * The list (and, when `:repoName` is present, detail) view for one run.
 *
 * Loads the run's `index.json` via {@link useRunLoader}, derives the displayed
 * rows with {@link applyQuery} over the query state decoded from the URL, and
 * wires the search/filter/sort controls to update that query state in the URL
 * (Req 8.12). Row activation and deep links open {@link RepoDetail}; a missing
 * or failed detail surfaces an "unavailable" notice and returns to the list
 * (Req 8.14).
 */
function RunPage() {
  const { runDate, runId, repoName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchFn } = useDashboardContext();

  // A repo whose detail was requested but is unavailable, surfaced on the list
  // after we navigate back (Req 8.14).
  const [unavailableRepo, setUnavailableRepo] = useState<string | null>(null);

  const selection = runDate && runId ? { runDate, runId } : null;
  const loader = useRunLoader(selection, { fetchFn });

  // The full query state lives in the URL query string (Req 8.12).
  const queryState = useMemo<QueryState>(
    () => decodeQueryState(searchParams.toString()),
    [searchParams],
  );

  const summaries = loader.index?.summaries ?? [];
  const results = useMemo(
    () => applyQuery(summaries, queryState),
    [summaries, queryState],
  );

  // ADDITIVE (Req 18.1, 18.6): classification-facet selections live in their
  // own additive state (kept out of the URL query state so the existing
  // `FilterState`/`QueryState` codec is untouched). Reset when the selection
  // changes so a prior run's selections never leak into a new run.
  const [classificationFilters, setClassificationFilters] =
    useState<ClassificationFilterState>(EMPTY_CLASSIFICATION_FILTERS);
  useEffect(() => {
    setClassificationFilters(EMPTY_CLASSIFICATION_FILTERS);
  }, [runDate, runId]);

  // ADDITIVE (Req 18.1): derive the selectable classification option lists from
  // the loaded Library document. `null` (no Library document) hides the facets
  // entirely so the panel renders exactly as today (graceful absence).
  const classificationOptions = useMemo(
    () => deriveClassificationOptions(loader.libraryIndex),
    [loader.libraryIndex],
  );

  // ADDITIVE (Req 18.1): narrow the queried rows by the active classification
  // facets, AFTER `applyQuery` so it composes (ANDs) with search/filter/sort.
  // This NO-OPs when no Library document is present: the join map is empty, so
  // we return the query results untouched and the list is exactly today's.
  const displayedRows = useMemo(
    () =>
      loader.libraryByKey.size > 0
        ? filterByClassification(
            results,
            loader.libraryByKey,
            classificationFilters,
          )
        : results,
    [results, loader.libraryByKey, classificationFilters],
  );

  // Distinct attribute values for the filter panel's language/visibility lists.
  const availableLanguages = useMemo(
    () => distinct(summaries.map((s) => s.primaryLanguage)),
    [summaries],
  );
  const availableVisibilities = useMemo(
    () => distinct(summaries.map((s) => s.visibility)),
    [summaries],
  );

  // Persist a new query state into the URL (replace so it doesn't spam history).
  const updateQuery = useCallback(
    (next: QueryState) => {
      setSearchParams(new URLSearchParams(encodeQueryState(next)), {
        replace: true,
      });
    },
    [setSearchParams],
  );

  const listPath = `/run/${runDate}/${runId}`;

  // Resolve the deep-linked / activated repo against the loaded index.
  const detailSummary: RepoSummary | null =
    repoName && loader.index
      ? (loader.index.summaries.find((s) => s.repoName === repoName) ?? null)
      : null;

  // The index finished loading but the requested repo is not in it (Req 8.14).
  const detailMissing =
    Boolean(repoName) && loader.status === "loaded" && detailSummary === null;

  // On a missing deep link, record the unavailable repo and return to the list.
  useEffect(() => {
    if (detailMissing && repoName) {
      setUnavailableRepo(repoName);
      navigate({ pathname: listPath, search: location.search }, { replace: true });
    }
  }, [detailMissing, repoName, listPath, location.search, navigate]);

  const goToList = useCallback(() => {
    navigate({ pathname: listPath, search: location.search });
  }, [navigate, listPath, location.search]);

  const onActivateRow = useCallback(
    (row: RepoSummary) => {
      setUnavailableRepo(null);
      navigate({
        pathname: `${listPath}/repo/${encodeURIComponent(row.repoName)}`,
        search: location.search,
      });
    },
    [navigate, listPath, location.search],
  );

  // Detail fetch failed for an in-index repo (Req 8.14): show notice + go back.
  const onDetailUnavailable = useCallback(() => {
    if (repoName) setUnavailableRepo(repoName);
    navigate({ pathname: listPath, search: location.search }, { replace: true });
  }, [repoName, navigate, listPath, location.search]);

  // --- Render the detail view when a repo is resolved (Req 8.13) ---
  if (repoName && detailSummary) {
    return (
      <RepoDetail
        key={detailSummary.detailKey}
        detailKey={detailSummary.detailKey}
        repoName={detailSummary.repoName}
        onClose={goToList}
        onUnavailable={onDetailUnavailable}
        fetchFn={fetchFn}
        // ADDITIVE (Req 18.4, 18.6): overlay the joined Library entry + the run
        // coordinates so the detail view can show the classification, metrics,
        // and narrative panels and fetch the per-repo Evidence/narrative
        // artifacts. `joinLibraryEntry` returns null when no Library document is
        // present (empty join map), so the panels degrade away (today's view).
        libraryEntry={joinLibraryEntry(detailSummary, loader.libraryByKey)}
        runDate={runDate}
        runId={runId}
      />
    );
  }

  // While a deep link is still resolving (index not yet loaded), show loading.
  if (repoName && !loader.index && loader.status === "loading") {
    return <p role="status">{RUN_LOADING_MESSAGE}</p>;
  }

  // --- Otherwise render the list view ---
  return (
    <section aria-label="Repository list view" className="app-list-layout">
      {/* Unavailable deep-link notice (Req 8.14). */}
      {unavailableRepo && (
        <p role="alert" className="app-error-banner">
          The requested repository "{unavailableRepo}" is unavailable.
        </p>
      )}

      {/* Completeness banner once an index is available (Req 2.2-2.6). */}
      {loader.index && (
        <CompletenessBanner
          loaded={loader.loadedCount}
          expectedRepoCount={loader.index.expectedRepoCount}
          org={loader.index.org}
          runDate={loader.index.runDate}
          unparseableCount={loader.unparseableCount}
        />
      )}

      {/* Full-failure error with the affected run and a bounded retry (Req 1.8, 11.4, 11.6). */}
      {loader.status === "error" && loader.error && (
        <p role="alert" className="app-error-banner">
          {loader.error.message}
          {loader.canRetry && (
            <button
              type="button"
              onClick={loader.retry}
              className="app-retry-button"
            >
              Retry
            </button>
          )}
        </p>
      )}

      {/* Loading indicator while the run is in flight (Req 1.4, 11.2, 12.4). */}
      {loader.status === "loading" && (
        <p role="status">{RUN_LOADING_MESSAGE}</p>
      )}

      {/* Search + filter + table, driven by applyQuery (Req 3-7). */}
      {loader.index && (
        <>
          {/* ADDITIVE (task 15.5; Req 16.2, 16.7, 18.1): an unobtrusive link to
              the applications & diagrams view, shown ONLY when this run has a
              Library document so the list renders exactly as today otherwise
              (graceful absence). Preserves the query string. */}
          {loader.libraryIndex && (
            <p className="app-list-actions">
              <Link
                to={{ pathname: `${listPath}/applications`, search: location.search }}
                className="app-applications-link"
              >
                View applications &amp; diagrams
              </Link>
            </p>
          )}
          <SearchBar
            value={queryState.searchTerm}
            onChange={(term) => updateQuery({ ...queryState, searchTerm: term })}
          />
          <FilterPanel
            filters={queryState.filters}
            onChange={(filters) => updateQuery({ ...queryState, filters })}
            availableLanguages={availableLanguages}
            availableVisibilities={availableVisibilities}
            classificationOptions={classificationOptions ?? undefined}
            classificationFilters={classificationFilters}
            onClassificationChange={setClassificationFilters}
          />
          <RepoTable
            rows={displayedRows}
            sort={queryState.sort}
            onSortChange={(sort) => updateQuery({ ...queryState, sort })}
            onActivateRow={onActivateRow}
            libraryByKey={loader.libraryByKey}
          />
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Applications page (ADDITIVE — task 15.5; Req 16.2, 16.7, 18.1)
// ---------------------------------------------------------------------------

/**
 * The applications & diagrams view for one run. Loads the run exactly like
 * {@link RunPage} via {@link useRunLoader} (so it reuses the same per-run
 * `index.json` + additive Library document load) and renders
 * {@link ApplicationView} from the loaded `libraryIndex`. Strictly additive: it
 * reads the Library document the loader already provides and the diagram
 * artifacts the Library shell already writes; it does not alter the list/detail
 * routes or their behavior. Degrades gracefully — when no Library document
 * exists, {@link ApplicationView} shows a friendly "no applications/diagrams"
 * message and a back link returns to the list.
 */
function ApplicationsPage() {
  const { runDate, runId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchFn } = useDashboardContext();

  const selection = runDate && runId ? { runDate, runId } : null;
  const loader = useRunLoader(selection, { fetchFn });

  const listPath = `/run/${runDate}/${runId}`;
  const goToList = useCallback(() => {
    navigate({ pathname: listPath, search: location.search });
  }, [navigate, listPath, location.search]);

  // While the run index is still loading we cannot yet know whether a Library
  // document exists; show the same loading indication as the list (Req 1.4).
  if (loader.status === "loading" && !loader.index) {
    return <p role="status">{RUN_LOADING_MESSAGE}</p>;
  }

  return (
    <section aria-label="Applications and diagrams view" className="app-list-layout">
      <ApplicationView
        runDate={runDate ?? ""}
        runId={runId ?? ""}
        libraryIndex={loader.libraryIndex}
        fetchFn={fetchFn}
        onClose={goToList}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Distinct, sorted, non-null values from a list (for filter option lists). */
function distinct(values: (string | null)[]): string[] {
  const set = new Set<string>();
  for (const value of values) {
    if (value !== null && value !== "") set.add(value);
  }
  return [...set].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}
